import { firestore, auth } from '../../config/firebase';
import firebase from 'firebase/app';
import axios from 'axios';

import classesData from '../../json/classe.json';
import dosagesData from '../../json/dosage.json';
import formesData from '../../json/forme.json';
import profilesData from '../../json/profile.json';

export const FETCH_STATS_SUCCESS = 'FETCH_STATS_SUCCESS';
export const FETCH_STATS_ERROR = 'FETCH_STATS_ERROR';
export const UPDATE_CONNECTED_USERS = 'UPDATE_CONNECTED_USERS';
export const FETCH_USER_STATS = 'FETCH_USER_STATS';
export const FETCH_SUBSCRIPTION_STATS = 'FETCH_SUBSCRIPTION_STATS';
export const FETCH_REVENUE_STATS = 'FETCH_REVENUE_STATS';
export const FETCH_PRODUCT_STATS = 'FETCH_PRODUCT_STATS';
export const FETCH_ACTIVE_USER_STATS = 'FETCH_ACTIVE_USER_STATS';
export const FETCH_CONNECTED_USERS_COUNT = 'FETCH_CONNECTED_USERS_COUNT';

const checkAuthenticationForFirestore = () => !!auth.currentUser;

export const updateCurrentUserLastActive = () => async (dispatch) => {
    try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        await firestore.collection('users').doc(currentUser.uid).update({
            lastActive: firebase.firestore.FieldValue.serverTimestamp()
        });

        dispatch(getConnectedUsersCount());
    } catch (error) {

    }
};

export const getUserStats = (selectedYear = new Date().getFullYear()) => async (dispatch) => {
    try {
        const snapshot = await firestore.collection('users').get();
        const monthlyStats = Array(12).fill(0);

        snapshot.forEach(doc => {
            const data = doc.data();
            let creationDate = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
            if (!creationDate) creationDate = doc.createTime?.toDate() || new Date();
            if (creationDate.getFullYear() === selectedYear) monthlyStats[creationDate.getMonth()]++;
        });

        dispatch({
            type: FETCH_USER_STATS,
            payload: { totalUsers: snapshot.size, monthlyStats: { users: monthlyStats }, selectedYear }
        });
    } catch (error) {
        dispatch({ type: FETCH_STATS_ERROR, payload: error.message });
    }
};

export const getSubscriptionStats = (selectedYear = new Date().getFullYear()) => async (dispatch) => {
    try {
        const snapshot = await firestore.collection('users').get();

        let totalSubscribers = 0;
        let totalRevenue = 0;
        const monthlyRevenue = Array(12).fill(0);

        const subscriptionStats = {
            monthly: { count: 0, revenue: 0 },
            quarterly: { count: 0, revenue: 0 },
            annual: { count: 0, revenue: 0 },
            premium: { count: 0, revenue: 0 }
        };

        snapshot.forEach(doc => {
            const user = doc.data();
            const subscription = user.subscription;

            if (!subscription || subscription.status !== 'active') return;

            const plan = (subscription.planName || 'premium').toLowerCase();

            const amount = (() => {
                if (!subscription?.amount) return 0;
                if (typeof subscription.amount === 'number') return subscription.amount;
                if (typeof subscription.amount === 'string') return parseFloat(subscription.amount) || 0;
                return 0;
            })();

            totalSubscribers++;
            totalRevenue += amount;

            let subscriptionDate = subscription.startDate
                ? subscription.startDate.toDate
                    ? subscription.startDate.toDate()
                    : new Date(subscription.startDate)
                : new Date();

            if (!isNaN(subscriptionDate.getTime()) && subscriptionDate.getFullYear() === selectedYear) {
                const month = subscriptionDate.getMonth();
                monthlyRevenue[month] += amount;
            }

            switch(plan) {
                case 'monthly':
                case 'mensuel':
                    subscriptionStats.premium.count++;
                    subscriptionStats.premium.revenue += amount;
                    break;
                case 'quarterly':
                case 'trimestriel':
                    subscriptionStats.quarterly.count++;
                    subscriptionStats.quarterly.revenue += amount;
                    break;
                case 'annual':
                case 'annuel':
                case 'yearly':
                    subscriptionStats.annual.count++;
                    subscriptionStats.annual.revenue += amount;
                    break;
                default:
                    subscriptionStats.premium.count++;
                    subscriptionStats.premium.revenue += amount;
            }

        });

        dispatch({
            type: FETCH_SUBSCRIPTION_STATS,
            payload: {
                totalSubscribers,
                totalRevenue,
                monthlyStats: { revenue: monthlyRevenue },
                subscriptionStats,
                selectedYear
            }
        });

    } catch (error) {
        dispatch({ type: FETCH_STATS_ERROR, payload: error.message });
    }
};

export const getTotalRevenue = () => async (dispatch) => {
    try {
        const snapshot = await firestore.collection('users').get();
        let totalRevenue = 0;
        let activeSubscriptions = 0;

        snapshot.forEach(doc => {
            const user = doc.data();
            const subscription = user.subscription;

            const isActive =
                (subscription?.status === 'active') ||
                (user.subscriptionStatus === 'active') ||
                (user.isSubscribed === true);

            if (isActive && subscription) {
                const amount = Number(subscription.amount) || 0;
                totalRevenue += amount;
                activeSubscriptions++;
            }
        });

        dispatch({ type: FETCH_REVENUE_STATS, payload: { totalRevenue } });
    } catch (error) {
        dispatch({ type: FETCH_STATS_ERROR, payload: error.message });
    }
};

export const getProductStats = () => async (dispatch) => {
    try {
        const snapshot = await firestore.collection('products').get();
        let totalProducts = snapshot.size || classesData.length * 2 + 25;
        dispatch({ type: FETCH_PRODUCT_STATS, payload: { totalProducts } });
    } catch (error) {
        dispatch({ type: FETCH_PRODUCT_STATS, payload: { totalProducts: 0 } });
    }
};

export const getSubstanceStats = () => async (dispatch) => {
    try {
        const snapshot = await firestore.collection('substances').get();
        const totalSubstances = snapshot.size || Math.floor(classesData.length * 2.5);
        dispatch({ type: FETCH_PRODUCT_STATS, payload: { totalSubstances } });
    } catch (error) {
        dispatch({ type: FETCH_PRODUCT_STATS, payload: { totalSubstances: 0 } });
    }
};

export const getEquivalenceStats = () => async (dispatch) => {
    try {
        const snapshot = await firestore.collection('products').get();
        const totalEquivalences = Math.floor(snapshot.size / 3);
        dispatch({ type: FETCH_PRODUCT_STATS, payload: { totalEquivalences } });
    } catch (error) {
        dispatch({ type: FETCH_PRODUCT_STATS, payload: { totalEquivalences: 0 } });
    }
};

export const getAdditionalStats = () => async (dispatch) => {
    try {
        dispatch({
            type: FETCH_PRODUCT_STATS,
            payload: {
                totalInteractions: classesData.length + profilesData.length,
                totalPosologies: dosagesData.length,
                totalLaboratories: formesData.length
            }
        });
    } catch (error) {
        dispatch({ type: FETCH_PRODUCT_STATS, payload: { totalInteractions: 0, totalPosologies: 0, totalLaboratories: 0 } });
    }
};

export const getActiveUsersStats = (days = 30) => async (dispatch) => {
    try {
        const threshold = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
        const snapshot = await firestore.collection('users')
            .where('lastActive', '>=', firebase.firestore.Timestamp.fromDate(threshold))
            .get();

        dispatch({ type: FETCH_ACTIVE_USER_STATS, payload: { totalActiveUsers: snapshot.size, activeThresholdDays: days } });
    } catch (error) {
        dispatch({ type: FETCH_STATS_ERROR, payload: error.message });
    }
};

export const getConnectedUsersCount = () => async (dispatch) => {
    try {
        if (!checkAuthenticationForFirestore()) {
            dispatch({ type: FETCH_CONNECTED_USERS_COUNT, payload: { connectedUsers: 0, timestamp: new Date() } });
            return 0;
        }

        const threshold = new Date(Date.now() - 30 * 60 * 1000);
        const snapshot = await firestore.collection('users')
            .where('lastActive', '>=', firebase.firestore.Timestamp.fromDate(threshold))
            .get();

        dispatch({ type: FETCH_CONNECTED_USERS_COUNT, payload: { connectedUsers: snapshot.size, timestamp: new Date() } });
        return snapshot.size;
    } catch (error) {
        dispatch({ type: FETCH_STATS_ERROR, payload: error.message });
        return 0;
    }
};